import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { SidebarItem, SidebarRoutingItem } from "@/constants/navigation";
import { AppTopbarSidebarToggle } from "@/layouts/AppLayout/AppTopbar";
import {
  BreadcrumbCrumb,
  BreadcrumbSeparator,
} from "@/layouts/AppLayout/AppTopbar/AppBreadcrumbs";
import { State } from "@/state";

type SettingsTopbarProps = {
  sidebarPanelRef: React.RefObject<
    import("react-resizable-panels").PanelImperativeHandle | null
  >;
  /**
   * The settings sidebar's items: the person's own sections, then one
   * overline per entity followed by its sections.
   */
  items: SidebarItem[];
};

type SettingsGroup = { name: string; sections: SidebarRoutingItem[] };

/**
 * Settings / <entity> / <section>, the same trail the app views show - or
 * Settings / <section> for one of the person's own, above the entities.
 * Built from the sidebar's items so the two can never disagree about what
 * sections an entity has.
 */
export function SettingsTopbar({
  sidebarPanelRef,
  items,
}: SettingsTopbarProps) {
  const isSidebarMinimized = State((store) => store.isSidebarMinimized);
  const setIsSidebarMinimized = State((store) => store.setIsSidebarMinimized);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { groups, ownSections } = useMemo(() => {
    const result: SettingsGroup[] = [];
    const own: SidebarRoutingItem[] = [];
    for (const item of items) {
      if (item.type === "overline") {
        result.push({ name: item.name, sections: [] });
      } else if (item.type === "routing" && item.path) {
        (result.length > 0 ? result[result.length - 1].sections : own).push(
          item,
        );
      }
    }
    return { groups: result, ownSections: own };
  }, [items]);

  const activeGroup = groups.find((group) =>
    group.sections.some((section) => section.path === pathname),
  );
  const activeSection = activeGroup?.sections.find(
    (section) => section.path === pathname,
  );
  const activeOwnSection = ownSections.find(
    (section) => section.path === pathname,
  );

  return (
    <div className="min-w-0 justify-start max-w-full grow flex flex-nowrap pl-2 pr-2 items-center gap-2">
      <AppTopbarSidebarToggle
        isSidebarMinimized={isSidebarMinimized}
        setIsSidebarMinimized={setIsSidebarMinimized}
        sidebarPanelRef={sidebarPanelRef}
        disableOpen={false}
      />
      <div className="flex items-center min-w-0 overflow-hidden">
        <BreadcrumbCrumb
          label="Settings"
          isSelected={!activeGroup && !activeOwnSection}
          onClick={() => navigate("/app/settings")}
        />
        {activeOwnSection && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbCrumb label={activeOwnSection.name} isSelected={true} />
          </>
        )}
        {activeGroup && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbCrumb
              label={activeGroup.name}
              isSelected={false}
              onClick={() => navigate(activeGroup.sections[0].path!)}
              options={groups.map((group) => ({
                id: group.sections[0].path!,
                label: group.name,
              }))}
              onSelectOption={(option) => navigate(option.id)}
            />
          </>
        )}
        {activeGroup && activeSection && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbCrumb
              label={activeSection.name}
              isSelected={true}
              options={activeGroup.sections.map((section) => ({
                id: section.path!,
                label: section.name,
              }))}
              onSelectOption={(option) => navigate(option.id)}
            />
          </>
        )}
      </div>
    </div>
  );
}
